import { MOCK_BOOKING_PAYMENT } from '../../../utilities/constants'
import { buildMockElectronPurchase } from './bookingPaymentMock'

/**
 * Pago vía Electron (IPC). Devuelve el mismo shape que `processPurchase` del pinpad web.
 * @param {{ reference: string, amount: unknown, session?: string, totalOriginal?: unknown }} input
 * @returns {Promise<{ success: boolean, charge: object, voucherPrinted?: boolean, voucherPrintError?: unknown }>}
 */
function formatMoneyString (v) {
  const n = Number(v)
  if (Number.isFinite(n)) return n.toFixed(2)
  return String(v ?? '').trim()
}

function isApprovedResponse (response) {
  const r = String(response ?? '').trim().toLowerCase()
  return r === 'approved' || r === 'aprobado' || r === '00'
}

function extractPurchaseResult (raw) {
  if (!raw || typeof raw !== 'object') return null
  if (raw.purchaseResult && typeof raw.purchaseResult === 'object') return raw.purchaseResult
  if ('success' in raw && 'charge' in raw) return raw
  return null
}

const DEFAULT_PAY_MESSAGE =
  'No se pudo completar el pago. Intenta nuevamente o usa otra tarjeta.'

function isNumericOnlyMessage (value) {
  const m = String(value ?? '').trim()
  return m !== '' && /^\d+$/.test(m)
}

/** Mensaje visible: NbError vía IPC; nunca solo un código numérico. */
function ipcPayUserMessage (raw) {
  const code = String(raw?.code ?? '').trim()
  const msg = String(raw?.message ?? '').trim()
  if (msg && !isNumericOnlyMessage(msg)) return msg
  if (code === 'BRIDGE_PAY_FAILED' || !msg || isNumericOnlyMessage(msg)) {
    return DEFAULT_PAY_MESSAGE
  }
  return msg || DEFAULT_PAY_MESSAGE
}

function throwCode (code, message) {
  const err = new Error(message || code)
  err.code = code
  throw err
}

export async function bookingElectronPay (input) {
  const reference = String(input?.reference ?? '')
  const sentAmount = input?.amount
  const session = input?.session
  const totalOriginal = input?.totalOriginal
  const startedAt = Date.now()

  if (MOCK_BOOKING_PAYMENT) {
    console.warn('[bookingElectronPay] MOCK_BOOKING_PAYMENT — sin IPC pinpad', {
      reference,
      amount: sentAmount,
      ts: Date.now(),
    })
    return buildMockElectronPurchase({ reference, amount: sentAmount })
  }

  console.log('[bookingElectronPay] start', {
    reference,
    amount: sentAmount,
    session,
    totalOriginal,
    ts: Date.now(),
  })

  if (typeof window === 'undefined' || !window.electronAPI || typeof window.electronAPI.bookingPay !== 'function') {
    throwCode('ELECTRON_IPC_FAILED', 'electronAPI.bookingPay no está disponible')
  }

  let raw
  try {
    raw = await window.electronAPI.bookingPay({
      reference,
      amount: sentAmount,
      session,
      totalOriginal,
    })
  } catch (e) {
    console.error('[bookingElectronPay] ipc_throw', {
      reference,
      code: e?.code,
      message: e?.message,
      elapsedMs: Date.now() - startedAt,
      ts: Date.now(),
    })
    const err = new Error(e?.message || 'Error en IPC bookingPay')
    err.code = e?.code != null && e.code !== '' ? String(e.code) : 'ELECTRON_IPC_FAILED'
    throw err
  }

  console.log('[bookingElectronPay] ipc_result', {
    reference,
    ok: raw?.ok,
    code: raw?.code,
    elapsedMs: Date.now() - startedAt,
    ts: Date.now(),
  })

  if (raw && typeof raw === 'object' && raw.ok === false) {
    const code = raw.code || 'ELECTRON_IPC_FAILED'
    const msg = ipcPayUserMessage(raw)
    throwCode(String(code), msg)
  }

  const purchaseResult = extractPurchaseResult(raw)
  if (!purchaseResult) {
    throwCode('ELECTRON_IPC_FAILED', 'Respuesta de Electron sin purchaseResult válido')
  }

  if (raw?.ok !== true) {
    throwCode('ELECTRON_IPC_FAILED', 'Respuesta de Electron sin ok === true')
  }

  if (purchaseResult.success !== true) {
    throwCode('TXN_NOT_APPROVED', 'La transacción no fue aprobada (success !== true)')
  }

  const charge = purchaseResult.charge
  if (!charge || typeof charge !== 'object') {
    throwCode('INVALID_CHARGE', 'Falta purchaseResult.charge')
  }

  const {
    auth,
    amount,
    reference: refCharge,
    cc_type: ccType,
    cc_number: ccNumber,
    foliocpagos: folioCPagos,
    response,
  } = charge

  if (
    auth == null || auth === '' ||
    amount == null || amount === '' ||
    !refCharge ||
    ccType == null || ccType === '' ||
    ccNumber == null || ccNumber === '' ||
    folioCPagos == null || folioCPagos === ''
  ) {
    throwCode('INVALID_CHARGE', 'charge incompleto: se requieren auth, amount, reference, cc_type, cc_number, foliocpagos')
  }

  if (String(refCharge) !== String(reference)) {
    throwCode('REFERENCE_MISMATCH', 'La referencia devuelta no coincide con la enviada')
  }

  const expectedAmountFormatted = formatMoneyString(sentAmount)
  if (formatMoneyString(amount) !== expectedAmountFormatted) {
    throwCode('AMOUNT_MISMATCH', 'El monto devuelto no coincide con el enviado')
  }

  if (!isApprovedResponse(response)) {
    throwCode('TXN_NOT_APPROVED', 'La transacción no está aprobada (response inválido)')
  }

  const mapped = {
    success: true,
    charge,
    voucherPrinted: purchaseResult.voucherPrinted,
    voucherPrintError: purchaseResult.voucherPrintError,
  }

  console.log('[bookingElectronPay] success', {
    reference,
    elapsedMs: Date.now() - startedAt,
    ts: Date.now(),
  })

  return mapped
}
